import qs from 'qs';
import { useState } from 'react';

import { useDialogContext } from '../../../contexts/DialogContext';
import useAsyncStatus from '../../../hooks/useAsyncStatus';
import { post } from '../../../redux/api';
import { ErrorAlert } from '../../generic';
import LoaderButton from '../../generic/Button/LoaderButton';
import Card from '../../generic/Card/Card';
import LabeledCheckbox from '../../generic/Forms/LabeledCheckbox';
import Text from '../../generic/Text';
import type { ReanalyzeQuickImportRequest } from '../../../../typings/api';
import type { Organization } from '../../../types';

export const FullOrganizationPackageCacheRebuildCard = ({ org }: { org: Organization }) => {
  const requestStatus = useAsyncStatus();
  const { onConfirm } = useDialogContext();
  const [shouldReduceConcurrency, setShouldReduceConcurrency] = useState(false);
  const onButtonClick = () => {
    onConfirm({
      title: 'Rebuild Organization Package Cache',
      body: (
        <div>
          This will also <b>turn on</b> the package index page for this organization. Are you sure you want to rebuild
          organization package cache for {org.title}?
        </div>
      ),
      onConfirm: async () => {
        try {
          requestStatus.onPending();
          await post<ReanalyzeQuickImportRequest, Record<string, never>>(
            `/api/packages/rebuild-cache?${qs.stringify({
              organizationId: org.id,
              reduceConcurrency: shouldReduceConcurrency,
            })}`,
            {},
          );
          requestStatus.onSuccess();
        } catch (e) {
          requestStatus.onError(e as Error);
        }
      },
    });
  };

  return (
    <Card isFullWidth padding="medium">
      <Card.Header>Rebuild Organization Package Cache</Card.Header>
      <Card.Content>
        <ErrorAlert status={{ error: requestStatus.status.error }} />
        <Text.Paragraph>This will fully rebuild the package cache for your current organization.</Text.Paragraph>
        <Text.Paragraph>
          Please note that rebuilding cache for some organizations may take a while. However, your existing data
          will not be affected and there will be no loss of functionality. You can check the status of the cache in
          the Faktory UI or the OrganizationPackages table.
        </Text.Paragraph>
        <div className="margin-bottom-md">
          <LabeledCheckbox
            label="Reduce concurrency (Warning: This will significantly increase processing time. Only use it if the cache job fails consistently.)"
            isChecked={shouldReduceConcurrency}
            onChange={() => {
              setShouldReduceConcurrency(!shouldReduceConcurrency);
            }}
              />
        </div>
        <LoaderButton isLoading={requestStatus.status.loading} onClick={onButtonClick}>
          Rebuild
        </LoaderButton>
      </Card.Content>
    </Card>
  );
};
